import React, { useEffect, useRef, useState } from "react";
import { CandidateProfile } from "../../../Redux/action/CandidateProfileAction";
import { ResumeUploadAction } from "../../../Redux/action/ResumeUploadAction";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { IoCloudUploadOutline } from "react-icons/io5";
import Swal from "sweetalert2";
import axios from "axios";
import ResumePreviewModal from "./ResumePreviewModal";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Divider,
  IconButton,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ProfileProgressBarAction } from "../../../Redux/action/ProfileProgressBarAction";

const PRIMARY = "#286a94";

const Resume = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const { userResult } = useSelector((state) => state.Auth);
  const userInfo = useSelector((state) => state?.Profile?.result);
  const candidateId = userResult?.userid;
  const baseUrl = process.env.REACT_APP_API_PATH;

  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  useEffect(() => {
    dispatch(CandidateProfile(candidateId));
  }, [dispatch, candidateId]);

  const handleChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const sizeMB = selectedFile.size / 1024 / 1024;

    if (sizeMB > 2) {
      toast.error("File size must be between 4KB and 2MB");
      e.target.value = null;
      return;
    }

    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Only PDF, DOC, or DOCX files are allowed.");
      e.target.value = null;
      return;
    }

    setFile(selectedFile);
  };

  const handleSubmit = async () => {
    if (!file) return toast.error("Resume is required!");

    let formData = new FormData();
    formData.append("candidateId", candidateId);
    formData.append("file", file);

    setUploading(true);
    setUploadProgress(0);

    try {
      await dispatch(ResumeUploadAction(formData, navigate, setUploadProgress));
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = null;
      await dispatch(CandidateProfile(candidateId));
      await dispatch(ProfileProgressBarAction(candidateId));
    } catch (error) {
      console.error("Upload failed", error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setFile(null);
    }
  };

  const handleDelete = () => {
    Swal.fire({
      title: "Delete Resume?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: PRIMARY,
    }).then(async (result) => {
      if (result.isConfirmed) {
        await axios.delete(
          `${baseUrl}/ebench/delete_resume_or_video?candidateId=${candidateId}&deleteResume=true`
        );
        dispatch(CandidateProfile(candidateId));
      }
    });
  };

  return (
    <>
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{ mb: 2, fontSize: { xs: "18px", sm: "20px" } }}
        color={PRIMARY}
      >
        Resume Upload
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {userInfo?.resumeFileUrl && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            background: "#eef5fa",
            borderRadius: 2,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
          }}
        >
          <Typography
            sx={{
              wordBreak: "break-all",
              fontSize: { xs: "14px", sm: "16px" },
            }}
          >
            <b>File:</b> {userInfo?.resumeFileName}
          </Typography>

          <Box
            display="flex"
            gap={1}
            justifyContent={{ xs: "flex-end", sm: "unset" }}
            width={{ xs: "100%", sm: "auto" }}
          >
            <IconButton onClick={() => setShowModal(true)} color="primary">
              <VisibilityIcon />
            </IconButton>
            <IconButton onClick={handleDelete} color="error">
              <DeleteIcon />
            </IconButton>
          </Box>
        </Paper>
      )}

      <Typography fontSize={14} fontWeight={600}>
        Upload Resume <span style={{ color: "red" }}>*</span>
      </Typography>

      <Box
        sx={{
          p: { xs: 2.5, sm: 4 },
          border: `2px dashed ${PRIMARY}`,
          borderRadius: 2,
          textAlign: "center",
          mt: 2,
          cursor: "pointer",
        }}
        onClick={() => fileInputRef.current.click()}
      >
        <IoCloudUploadOutline size={40} color={PRIMARY} />

        <Typography
          sx={{
            mt: 1,
            color: PRIMARY,
            fontSize: { xs: "13px", sm: "14px" },
            wordBreak: "break-word",
          }}
        >
          {file ? file.name : "Upload PDF, DOC, or DOCX"}
        </Typography>
      </Box>

      <input
        hidden
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        onChange={handleChange}
      />

      {uploading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography
            textAlign="center"
            sx={{ mt: 1, fontSize: { xs: "12px", sm: "14px" } }}
          >
            Uploading... {uploadProgress}%
          </Typography>
        </Box>
      )}

      {file && !uploading && (
        <Button
          sx={{
            mt: 3,
            bgcolor: PRIMARY,
            width: { xs: "100%", sm: "auto" },
          }}
          variant="contained"
          onClick={handleSubmit}
        >
          Upload Resume
        </Button>
      )}

      {/* 🔍 Preview Modal */}
      <ResumePreviewModal
        open={showModal}
        onClose={() => setShowModal(false)}
        fileUrl={userInfo?.resumeFileUrl}
        uploading={uploading}
        progress={uploadProgress}
        fileName={userInfo?.resumeFileName}
      />
    </>
  );
};

export default Resume;
